import MenuDropdown from "@Components/MenuDropdown";
import { Fragment, useEffect, useState } from "react";
import Image from "@Components/Image";
import CustomLink from "@Components/CustomLink";
import EmailDialog from "@Components/EmailDialog/EmailDialog";
import config from "@Config/index";
import styles from "./Navbar.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import BurgerIcon from "@Icons/BurgerIcon";
import CloseIcon from "@Icons/CloseIcon";
import ArrowLeftIcon from "@Icons/ArrowLeftIcon";
import useTranslationHook from "hooks/useTranslationHook";
import useAppPadding from "hooks/useAppPadding";
import { useHistory } from "hooks/useHistory";
import { webAppBackHandler } from "@Utils/webApputils";

const logoDark = `${config.IMAGE_STATIC_ASSETS_COMPONENTS_PATH}/Header/assets/amber-logo-dark.svg`;
const logoLight = `${config.IMAGE_STATIC_URL}/images/logo/amber-logo-light.svg`;

// Fallback only. Every v2 page now passes its own `links` — the two most useful next
// reads from wherever you are — because one shared pair meant the nav said the same thing
// on all ten and told you nothing about where you were. Kept as a default so a new page
// that forgets to pass links still renders something sane rather than an empty bar.
const DEFAULT_NAV_LINKS = [
  { label: "navbar.v2.blogs", href: "/blog" },
  { label: "navbar.v2.career", href: "/career-v2" },
];

export type NavBrand = "b2c" | "b2b";

interface NavbarProps {
  /**
   * App WebView treatment for pages that keep this header: hide the website wordmark and
   * place the remaining controls below the native status bar.
   */
  isAppHeader?: boolean;
  /** Add the WebView-provided top inset when the native container does not already own it. */
  addAppPadding?: boolean;
  /**
   * Which audience's CTA palette. `b2c` is the pink gradient pill on the
   * student-facing pages; `b2b` is the solid blue pill on List With Us and
   * Partner With Us. See theme/buttons.scss — this is the one prop that decides
   * which side of the product the bar belongs to.
   */
  brand?: NavBrand;
  /**
   * 14px/500 nav links instead of the default 16px/600. The partner pages set
   * this by hand in their own Header stylesheets before this prop existed.
   */
  compactLinks?: boolean;
  /**
   * CSS selector for a sentinel element — usually the hero. While the sentinel's
   * midpoint is still below the top of the viewport the CTA stays collapsed
   * (zero width, no clicks, no tab stop), then it opens out.
   *
   * The point is not the animation: it is that the bar should not repeat the
   * hero's CTA while the hero is still showing it. Both partner pages did this
   * in their own Header components before this prop existed.
   *
   * Applies at EVERY width, mobile included. The reason is the same on a phone
   * as on a desktop — while the hero is on screen it is showing this exact CTA,
   * so the bar repeating it is noise. On a phone in the hero the bar is
   * therefore wordmark + menu, and the CTA slides in once the hero is behind you.
   */
  ctaRevealAfter?: string;
  /**
   * Override the nav links. Defaults to Blogs + Career.
   *
   * `label` is a TRANSLATION KEY, not display text — the bar runs it through
   * `t()` so a page never has to hold a `useTranslationHook` just to name its
   * links. Legacy English-as-key strings still fall back to themselves.
   *
   * A link may carry an `onClick` instead of navigating — the partner pages need
   * this for "Login", which opens a modal rather than going to a URL (there is no
   * /login route). `href` stays required so the link is still a real anchor with a
   * sane fallback if JS has not run.
   */
  links?: {
    label: string;
    href: string;
    onClick?: () => void;
    /**
     * Renders this link as a DROPDOWN target instead of a plain link, with `menu` as
     * the panel. Used for "Support", which on the partner pages is not a page at all —
     * it is a menu of contact routes (chat widget, WhatsApp, Messenger, the phone
     * number for the reader's country, email). `href` is still required and is what a
     * no-JS reader gets.
     *
     * Desktop only. The mobile sheet keeps the plain link, because a dropdown inside a
     * slide-over is a second layer of overlay for no gain — the sheet has the room to
     * just list the same routes if that is ever wanted.
     */
    menu?: React.ReactNode;
    /**
     * Optional mark rendered after the label on a `menu` link — a dropdown chevron.
     * Called with the menu's open state so it can reflect it.
     *
     * Supplied BY THE PAGE rather than drawn here on purpose: the animated version is a
     * Lottie, and its host lives under `pages/`. Importing that up into a shared
     * component would point `components/` at `pages/`, which is backwards. The Navbar
     * only decides where the mark goes.
     */
    menuIcon?: (isOpen: boolean) => React.ReactNode;
    /**
     * Forces a full document load instead of a client-side route change.
     *
     * Needed when the path is served by a DIFFERENT app than this one. `/about` is
     * the case that prompted it: nginx routes it to the legacy s.web build in
     * production, but react-router matches this app's own /about route first, so an
     * in-bar click rendered our page while a fresh load of the same URL got s.web's.
     * A real <a> hands the URL back to the server and lets it decide.
     *
     * Off by default — a client-side transition is the right thing for any path this
     * app actually owns.
     */
    redirect?: boolean;
  }[];
  /** CTA label, as a translation key. Defaults to "Visit amber". */
  ctaLabel?: string;
  /** CTA target. Defaults to "/". */
  ctaHref?: string;
  /**
   * Called instead of navigating, for a CTA that opens a form rather than a page.
   * When present the CTA still renders as the same pill; only its behaviour changes.
   */
  ctaOnClick?: () => void;
  /**
   * When true the CTA starts as a soft grey secondary pill and only turns into
   * the pink primary gradient once the page is scrolled. Defaults to false —
   * the CTA is the pink primary at every scroll position.
   */
  ctaSecondaryUntilScroll?: boolean;
  /**
   * Renders the "Visit amber" CTA as the secondary (hairline) button instead of the
   * gradient pill. For pages whose own hero CTA is the primary action — two filled
   * pills on one screen leaves neither reading as the main one.
   *
   * Optional and defaulting to false, so every page that already renders <Navbar />
   * keeps the gradient pill it has today.
   *
   * Distinct from `ctaSecondaryUntilScroll`, which is scroll-dependent: this one is
   * secondary at every scroll position. Setting both makes this one win.
   */
  secondaryCta?: boolean;
}

const Navbar = ({
  isAppHeader = false,
  addAppPadding = true,
  brand = "b2c",
  compactLinks = false,
  ctaRevealAfter,
  links = DEFAULT_NAV_LINKS,
  ctaLabel = "navbar.v2.cta.visitamber",
  ctaHref = "/",
  ctaOnClick,
  ctaSecondaryUntilScroll = false,
  secondaryCta = false,
}: NavbarProps) => {
  const { t } = useTranslationHook();
  const history = useHistory();
  const appPadding = useAppPadding();
  // `true` when a dark-themed section is currently behind the header.
  const [onDark, setOnDark] = useState(false);
  // `true` once the user has scrolled past the very top — the glass effect
  // (blur, tint, shadow) only appears after this; at the top the header is plain.
  const [scrolled, setScrolled] = useState(false);
  // The mobile menu. Below `sm` the bar carries only the wordmark, one CTA and
  // this; the nav links move into the panel it opens.
  const [menuOpen, setMenuOpen] = useState(false);
  // ⚠️  `mailto:` LINKS OPEN A CHOICE, NOT THE MAIL APP. A bare mailto does nothing visible for
  // anyone without a configured mail client, or where a browser or company policy blocks the
  // handler — so the reader clicks "Email us" and nothing happens. Any nav link whose href is a
  // `mailto:` opens `EmailDialog` instead: the address, a copy button and the mailto as a real
  // link. Holds the bare address while the dialog is up.
  const [emailFor, setEmailFor] = useState<string | null>(null);
  const mailAddress = (href: string) =>
    href.startsWith("mailto:") ? decodeURIComponent(href.slice(7).split("?")[0]) : null;
  // ── The CTA's reveal ──────────────────────────────────────────────────────
  // On the MOBILE bar the CTA is always gated: the bar is wordmark + CTA + menu
  // once you are past the hero, and wordmark + menu while you are still in it.
  // The hero is showing this exact CTA at that point, so the bar repeating it is
  // noise on the one screen size with no room to spare.
  //
  // On DESKTOP it is gated only for pages that pass `ctaRevealAfter` — the two
  // partner pages, which is the behaviour they already had. The eight
  // student-facing pages keep their always-visible CTA on a wide screen.
  //
  // Starts revealed so a desktop first paint has no flash; the effect below
  // corrects it on mobile before anything is interactive.
  const [ctaRevealed, setCtaRevealed] = useState(true);
  const [isMobileBar, setIsMobileBar] = useState(false);

  // While the panel is open the page behind it must not scroll, and Escape must
  // close it — a full-screen overlay with no keyboard exit is a trap.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    // Set directly rather than via the app shell's `bodyLocked` class: that class
    // lives in a CSS module, so its real name is hashed at build time and cannot
    // be applied by string from here.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  // A resize up to desktop hides the burger, so a panel left open would be
  // stranded with no way to close it.
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 650) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Watches whatever gates the CTA.
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      // 650 is `sm` in theme/responsive.scss — the width where the bar drops its
      // links and becomes wordmark + CTA + menu.
      const mobile = window.innerWidth < 650;
      setIsMobileBar(mobile);

      // Desktop, and the page did not opt in: the CTA is simply always there.
      if (!mobile && !ctaRevealAfter) {
        setCtaRevealed(true);
        return;
      }

      // The sentinel is the page's hero. Pages that name one get that; the rest
      // fall back to their first <section>, which on every page here IS the hero.
      // Read live rather than cached: a hero's own scroll effect changes its
      // height over the first few hundred px, so a value measured on mount
      // would be stale.
      const sentinel = document.querySelector<HTMLElement>(ctaRevealAfter || "section");
      if (!sentinel) {
        setCtaRevealed(true);
        return;
      }
      const rect = sentinel.getBoundingClientRect();
      setCtaRevealed(rect.top + rect.height / 2 <= 0);
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(check);
    };

    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [ctaRevealAfter]);

  useEffect(() => {
    // Sections that opt into the dark header treatment are marked with the
    // attribute data-nav-theme="dark" (see the CrewCTA section).
    const darkSections = Array.from(
      document.querySelectorAll<HTMLElement>('[data-nav-theme="dark"]'),
    );

    // A dark section is "behind the header" while its top has scrolled above the
    // 64px header line and its bottom is still below it. We track that (plus the
    // scrolled flag) on scroll, rAF-throttled.
    const HEADER = 64;
    let raf = 0;
    const check = () => {
      raf = 0;
      setScrolled(window.scrollY > 8);
      const covering = darkSections.some((el) => {
        const r = el.getBoundingClientRect();
        return r.top <= HEADER && r.bottom >= HEADER;
      });
      setOnDark(covering);
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(check);
    };

    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  // Which of the six CTA classes applies. Kept out of the JSX because the
  // precedence between the three treatments is the part worth reading:
  //
  //   secondaryCta            always the hairline chip (and it inverts on dark)
  //   ctaSecondaryUntilScroll soft grey at the top of the page, brand fill once
  //                           scrolled
  //   neither                 the brand fill throughout
  //
  // `secondaryCta` wins if both are set.
  const b = brand === "b2b" ? "B2b" : "";
  let ctaClassName: string;
  if (secondaryCta) {
    ctaClassName = onDark ? styles[`secondaryButtonOnDark${b}`] : styles[`secondaryButton${b}`];
  } else if (ctaSecondaryUntilScroll && !scrolled) {
    ctaClassName = styles[`tryButtonSecondary${b}`];
  } else {
    ctaClassName = styles[`tryButton${b}`];
  }

  const appHeaderPadding = isAppHeader && addAppPadding ? appPadding : 0;

  return (
    <>
      <nav
        className={`${styles.navbar} ${scrolled ? styles.scrolled : ""} ${
          onDark ? styles.onDark : ""
        } ${menuOpen ? styles.menuOpenBar : ""} ${isAppHeader ? styles["app-header"] : ""}`}
        style={
          appHeaderPadding
            ? {
                height: `${64 + appHeaderPadding}px`,
                paddingTop: `${12 + appHeaderPadding}px`,
              }
            : undefined
        }
      >
        {isAppHeader && (
          <button
            type="button"
            className={styles["app-back-button"]}
            onClick={() => webAppBackHandler(history)}
            aria-label="Back"
          >
            <ArrowLeftIcon width="20" height="20" />
          </button>
        )}
        {!isAppHeader && (
          <CustomLink
            href="/"
            className={`${styles.brand} ${styles.above}`}
            dataTestId="v2-navbar-logo"
          >
            <Image
              src={onDark ? logoLight : logoDark}
              alt="amber"
              className={styles.logo}
              width={100}
              height={24}
              isEagerLoad
            />
          </CustomLink>
        )}

        <div className={`${styles.links} ${styles.above}`}>
          {links.map((link) => {
            const anchor = (isOpen = false) => (
              <CustomLink
                href={link.href}
                redirect={link.redirect}
                onClick={
                  link.onClick || link.menu || mailAddress(link.href)
                    ? (e: React.MouseEvent) => {
                        e.preventDefault();
                        if (link.onClick) link.onClick();
                        else if (mailAddress(link.href)) setEmailFor(mailAddress(link.href));
                      }
                    : undefined
                }
                className={`${styles.link} ${compactLinks ? styles.linkCompact : ""} ${
                  link.menu ? styles.linkMenu : ""
                }`}
              >
                {t(link.label)}
                {link.menuIcon ? link.menuIcon(isOpen) : null}
              </CustomLink>
            );

            // A menu link is the same anchor, wrapped — so it keeps the bar's type,
            // spacing and inverting colour rather than becoming a second kind of thing.
            return link.menu ? (
              <MenuDropdown
                key={link.href}
                direction="left"
                renderTarget={(isOpen) => anchor(isOpen)}
                menu={link.menu}
                // Keyed off the href, not the label: the label is a translation key
                // now, so deriving the test id from it would put a dotted key (or,
                // once translated, a localised word) into the selector.
                dataTestId={`v2-navbar-menu-${link.href.replace(/^\//, "") || "home"}`}
              />
            ) : (
              <Fragment key={link.href}>{anchor()}</Fragment>
            );
          })}
          {/*
          Three CTA treatments, in precedence order:

          - `secondaryCta` — always the hairline chip. The primary is a saturated
            gradient pill that reads on either ground, so it needs no dark variant;
            the hairline chip's ink and ring are both near-black, so it has to invert
            with the bar — the same switch the logo above makes.
          - `ctaSecondaryUntilScroll` — soft grey pill at the top of the page, pink
            primary once scrolled.
          - neither — pink primary throughout.
        */}
          <CustomLink
            href={ctaHref}
            onClick={
              ctaOnClick
                ? (e: React.MouseEvent) => {
                    e.preventDefault();
                    ctaOnClick();
                  }
                : undefined
            }
            className={`${ctaClassName} ${styles.ctaReveal} ${
              ctaRevealAfter ? styles.ctaRevealGated : ""
            } ${ctaRevealed ? styles.ctaRevealShown : ""}`}
            dataTestId="v2-navbar-cta"
            // Inert while collapsed: no clicks, no tab stop, not announced. It is
            // collapsed whenever it is gated and not yet revealed — on mobile
            // always, on desktop only for pages that opted in.
            tabIndex={!ctaRevealed && (isMobileBar || ctaRevealAfter) ? -1 : undefined}
            aria-hidden={!ctaRevealed && (isMobileBar || ctaRevealAfter) ? true : undefined}
          >
            {t(ctaLabel)}
          </CustomLink>

          {/* Below `sm` this is the only other thing in the bar. A real <button>,
            not a styled div, so it is in the tab order and answers to Space. */}
          {!isAppHeader && (
            <button
              type="button"
              className={styles.menuButton}
              aria-label={menuOpen ? t("navbar.v2.menu.close") : t("navbar.v2.menu.open")}
              aria-expanded={menuOpen}
              aria-controls="v2-nav-menu"
              onClick={() => setMenuOpen((open) => !open)}
              data-testid="v2-navbar-menu"
            >
              {menuOpen ? <CloseIcon /> : <BurgerIcon />}
            </button>
          )}
        </div>

        {/* The panel. Rendered inside <nav> so it inherits the bar's stacking
          context and sits above the page without a portal. */}
        <div
          id="v2-nav-menu"
          className={`${styles.menuPanel} ${menuOpen ? styles.menuPanelOpen : ""}`}
          // Not the `hidden` ATTRIBUTE: that is only a UA `display: none`, and this
          // panel sets `display: flex` for its layout, which overrides it. The
          // closed panel then stayed in the layout with its links focusable and
          // clickable over the page. Hidden with `visibility` in CSS instead, which
          // takes it out of hit-testing and the tab order AND still transitions.
          aria-hidden={!menuOpen}
        >
          {links.map((link) => (
            <CustomLink
              key={link.href}
              href={link.href}
              redirect={link.redirect}
              className={styles.menuLink}
              onClick={(e: React.MouseEvent) => {
                setMenuOpen(false);
                if (link.onClick) {
                  e.preventDefault();
                  link.onClick();
                } else if (mailAddress(link.href)) {
                  e.preventDefault();
                  setEmailFor(mailAddress(link.href));
                }
              }}
            >
              {t(link.label)}
            </CustomLink>
          ))}
        </div>

        {/* Tapping away closes it. `aria-hidden` because the button above already
          exposes the control; this is a convenience, not a second control. */}
        <div
          className={`${styles.menuScrim} ${menuOpen ? styles.menuScrimOpen : ""}`}
          style={appHeaderPadding ? { top: `${64 + appHeaderPadding}px` } : undefined}
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      </nav>
      {emailFor && <EmailDialog email={emailFor} onClose={() => setEmailFor(null)} />}
      {appHeaderPadding > 0 && (
        <div
          className={styles["app-header-offset"]}
          style={{ height: `${appHeaderPadding}px` }}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default wrapperHOC(Navbar, {
  componentName: "Navbar",
  showForChina: true,
});
